import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Layout, PageHero } from "../components/Layout";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/motion";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { BLOGS } from "../data";
import api from "../lib/api";

export default function Blog() {
  const [allBlogs, setAllBlogs] = useState(BLOGS);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");

  const fixImgUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    return `${backendUrl}${url}`;
  };

  useEffect(() => {
    api.get("/blogs").then((res) => {
      if (res.data && res.data.length > 0) {
        // Filter out unpublished drafts for public visitors
        setAllBlogs(res.data.filter((b) => b.published !== false));
      }
    }).catch(() => {});
  }, []);

  const cats = ["All", ...Array.from(new Set(allBlogs.map((b) => b.category).filter(Boolean)))];
  const items = allBlogs.filter((b) =>
    (cat === "All" || b.category === cat) &&
    (b.title.toLowerCase().includes(q.toLowerCase()) || (b.excerpt || "").toLowerCase().includes(q.toLowerCase())));

  return (
    <Layout>
      <Seo title="Blog - AI, Software & Tech Insights" description="Read expert insights on AI development, software engineering, cloud architecture, ERP, HRMS, CRM, and digital transformation from Neuravolt's engineering team." keywords="AI blog, software engineering insights, tech blog India, digital transformation, cloud architecture, ERP insights, Neuravolt blog" path="/blog" />
      <PageHero eyebrow="Journal" title="Insights & ideas" sub="Thoughts on AI, engineering and building great software." />

      <section className="py-16 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row gap-4 sm:items-center justify-between mb-10">
            <div className="flex flex-wrap gap-2">
              {cats.map((c) => (
                <button key={c} data-testid={`blog-cat-${c}`} onClick={() => setCat(c)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${cat === c ? "bg-[#FF5A1F] text-white" : "bg-white border border-black/5 text-neutral-600 hover:text-[#FF5A1F]"}`}>{c}</button>
              ))}
            </div>
            <div className="relative sm:w-72">
              <Search className="w-4 h-4 text-neutral-400 absolute left-4 top-1/2 -translate-y-1/2" />
              <input data-testid="blog-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles..."
                className="w-full bg-white border border-black/10 rounded-full pl-10 pr-4 py-2.5 text-sm outline-none focus:border-[#FF5A1F] transition-colors" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((b, i) => (
              <Reveal key={b.id || b.slug} delay={(i % 3) * 0.06}>
                <Link to={`/blog/${b.slug}`}>
                  <motion.div whileHover={{ y: -6 }} data-testid={`blog-${i}`} className="group card-soft rounded-3xl overflow-hidden h-full flex flex-col">
                    <div className="h-48 overflow-hidden bg-neutral-100">
                      {b.img ? (
                        <img src={fixImgUrl(b.img)} alt={b.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full grid place-items-center text-neutral-300 text-sm font-semibold uppercase tracking-wider">Neuravolt Article</div>
                      )}
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-2 text-xs text-neutral-400 mb-2">
                        <span className="text-[#FF5A1F] font-semibold">{b.category || "General"}</span> · {b.read || "5 min"}
                      </div>
                      <h3 className="font-heading text-lg font-semibold text-[#0B0B0B] leading-snug mb-2">{b.title}</h3>
                      <p className="text-neutral-500 text-sm line-clamp-2 flex-1">{b.excerpt}</p>
                    </div>
                  </motion.div>
                </Link>
              </Reveal>
            ))}
          </div>
          {items.length === 0 && <p className="text-center text-neutral-400 py-16">No articles found.</p>}
        </div>
      </section>
    </Layout>
  );
}
